/**
 * Getting markup that is not an element back out of a React tree.
 *
 * `htmlRaw` holds things React has no way to emit: a `<style>` block, and the
 * conditional comments an email designer writes for Outlook. React escapes
 * text, and `dangerouslySetInnerHTML` needs a host element to hang the content
 * on — a wrapping `<div>` would change where an Outlook-only table sits, which
 * is the one thing the conditional exists to control.
 *
 * So the renderer emits an empty, self-closing placeholder carrying the markup
 * in an attribute, and the markup is put back into the finished string. The
 * placeholder has no children, so finding it again is a match on one tag rather
 * than a search for a balancing close tag through arbitrary nested markup.
 */
export const RAW_MARKUP_ATTRIBUTE = "data-email-raw";

const PLACEHOLDER = new RegExp(
  `<span ${RAW_MARKUP_ATTRIBUTE}="([^"]*)"></span>`,
  "g",
);

/** Undo the escaping React applies to an attribute value. */
function decodeAttribute(value: string): string {
  return value
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");
}

/**
 * Replace every `htmlRaw` placeholder with the markup it stands for.
 *
 * Call this on the rendered HTML — and only on HTML. The plain-text render has
 * no markup to restore, and putting a `<style>` block back into it would be
 * putting CSS in front of a reader.
 */
export function restoreRawMarkup(html: string): string {
  return html.replace(PLACEHOLDER, (_match, encoded: string) =>
    decodeAttribute(encoded),
  );
}
